import { MultiFactorInfo } from 'firebase/auth';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import {
  IIdentityTenant,
  IPermission,
  ISignInProvider,
} from '../../../../data/types';
import { LoginState } from '../Login';
import { Authenticate } from './Authenticate';
import { ForgotPassword } from './ForgotPassword';
import { Identify } from './Identify';
import { OrganizationSelection } from './OrganizationSelection';
import { ProviderSelection } from './ProviderSelection';
import { SecondFactor } from './SecondFactor/SecondFactor';
import { SecondFactorVerification } from './SecondFactorVerification';
import { SSOAuth } from './SSOAuth';
import { TenantSelection } from './TenantSelection';
import { VerifyingDevice } from './VerifyingDevice';
import { UnableToSendVerification } from './UnableToSendVerification';

/**
 * Props for the LoginStepRenderer component
 */
export interface ILoginStepRendererProps {
  loginState: LoginState;
  email: string;
  password: string;
  error: string;
  verificationCode: string;
  showResendSuccess: boolean;
  resetSent: boolean;
  multiFactorHints: MultiFactorInfo[];
  selectedFactor: MultiFactorInfo | null;
  selectedProvider: string | null;
  selectedSSOMethod: string | null;
  selectedTenant: string | null;
  selectedOrganization: string | null;
  signInProviders: ISignInProvider[];
  userTenants: IIdentityTenant[];
  userOrganizations: IPermission[];
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onVerificationCodeChange: (code: string) => void;
  onSelectFactor: (factor: MultiFactorInfo) => void;
  onSelectProvider: (provider: string | null) => void;
  onSelectSSOMethod: (method: string | null) => void;
  onSelectTenant: (orgId: string | null) => void;
  onSelectOrganization: (orgId: string | null) => void;
  onStartOver: () => void;
  onForgotPassword: () => void;
}

// Fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated wrapper for step transitions
const AnimatedStepWrapper = styled.div`
  animation: ${fadeIn} 300ms ease-in-out;
  width: 100%;
`;

/**
 * LoginStepRenderer component - Renders the appropriate login step based on current state
 * Uses strategy pattern to map login states to their corresponding components
 */
export const LoginStepRenderer = React.memo(
  (props: ILoginStepRendererProps): React.ReactElement | null => {
    // Strategy map: renders the appropriate component for each login state
    const stepComponents: Record<LoginState, () => React.ReactElement | null> =
      {
        [LoginState.IDENTIFY]: (): React.ReactElement => (
          <Identify
            email={props.email}
            onEmailChange={props.onEmailChange}
            error={props.error}
          />
        ),

        [LoginState.SELECT_TENANT]: (): React.ReactElement => (
          <TenantSelection
            tenants={props.userTenants}
            selectedTenant={props.selectedTenant}
            onSelectTenant={props.onSelectTenant}
            error={props.error}
          />
        ),

        [LoginState.SELECT_PROVIDER]: (): React.ReactElement => (
          <ProviderSelection
            signInProviders={props.signInProviders}
            selectedProvider={props.selectedProvider}
            onSelectProvider={props.onSelectProvider}
            error={props.error}
          />
        ),

        [LoginState.AUTHENTICATE]: (): React.ReactElement => (
          <Authenticate
            password={props.password}
            email={props.email}
            onPasswordChange={props.onPasswordChange}
            onForgotPassword={props.onForgotPassword}
            error={props.error}
          />
        ),

        [LoginState.FORGOT_PASSWORD]: (): React.ReactElement => (
          <ForgotPassword email={props.email} resetSent={props.resetSent} />
        ),

        [LoginState.SECOND_FACTOR_REQUIRED]: (): React.ReactElement => (
          <SecondFactor
            hints={props.multiFactorHints}
            onSelectFactor={props.onSelectFactor}
            selectedFactor={props.selectedFactor}
            error={props.error}
          />
        ),

        [LoginState.VERIFYING_DEVICE_FOR_MFA]: (): React.ReactElement => (
          <VerifyingDevice />
        ),

        [LoginState.SECOND_FACTOR_VERIFICATION]: (): React.ReactElement => (
          <SecondFactorVerification
            verificationCode={props.verificationCode}
            onVerificationCodeChange={props.onVerificationCodeChange}
            selectedFactor={props.selectedFactor}
            showResendSuccess={props.showResendSuccess}
            error={props.error}
          />
        ),

        [LoginState.UNABLE_TO_SEND_VERIFICATION]: (): React.ReactElement => (
          <UnableToSendVerification onStartOver={props.onStartOver} />
        ),

        [LoginState.SSO_AUTH]: (): React.ReactElement => (
          <SSOAuth
            signInProviders={props.signInProviders}
            selectedSSOMethod={props.selectedSSOMethod}
            onSelectSSOMethod={props.onSelectSSOMethod}
            error={props.error}
          />
        ),

        [LoginState.SELECT_ORGANIZATION]: (): React.ReactElement => (
          <OrganizationSelection
            organizations={props.userOrganizations}
            selectedOrganization={props.selectedOrganization}
            onSelectOrganization={props.onSelectOrganization}
            error={props.error}
          />
        ),
      };

    const renderStep = stepComponents[props.loginState];
    return renderStep ? (
      <AnimatedStepWrapper key={props.loginState}>
        {renderStep()}
      </AnimatedStepWrapper>
    ) : null;
  },
  (
    prevProps: Readonly<ILoginStepRendererProps>,
    nextProps: Readonly<ILoginStepRendererProps>
  ): boolean => {
    // Only re-render if loginState or relevant data changes
    return (
      prevProps.loginState === nextProps.loginState &&
      prevProps.email === nextProps.email &&
      prevProps.password === nextProps.password &&
      prevProps.error === nextProps.error &&
      prevProps.verificationCode === nextProps.verificationCode &&
      prevProps.showResendSuccess === nextProps.showResendSuccess &&
      prevProps.resetSent === nextProps.resetSent &&
      prevProps.multiFactorHints === nextProps.multiFactorHints &&
      prevProps.selectedFactor === nextProps.selectedFactor &&
      prevProps.selectedProvider === nextProps.selectedProvider &&
      prevProps.signInProviders === nextProps.signInProviders &&
      prevProps.selectedSSOMethod === nextProps.selectedSSOMethod &&
      prevProps.selectedTenant === nextProps.selectedTenant &&
      prevProps.selectedOrganization === nextProps.selectedOrganization &&
      prevProps.userTenants === nextProps.userTenants &&
      prevProps.userOrganizations === nextProps.userOrganizations &&
      prevProps.onEmailChange === nextProps.onEmailChange &&
      prevProps.onPasswordChange === nextProps.onPasswordChange &&
      prevProps.onVerificationCodeChange ===
        nextProps.onVerificationCodeChange &&
      prevProps.onSelectFactor === nextProps.onSelectFactor &&
      prevProps.onSelectProvider === nextProps.onSelectProvider &&
      prevProps.onSelectSSOMethod === nextProps.onSelectSSOMethod &&
      prevProps.onSelectTenant === nextProps.onSelectTenant &&
      prevProps.onSelectOrganization === nextProps.onSelectOrganization &&
      prevProps.onStartOver === nextProps.onStartOver &&
      prevProps.onForgotPassword === nextProps.onForgotPassword
    );
  }
);

LoginStepRenderer.displayName = 'LoginStepRenderer';
